import { useState } from "react";
import axios from "axios";
import { Mail, Globe, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/motion/Reveal";
import { SITE } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initial = { name: "", email: "", organisation: "", subject: "", message: "", website: "" };

export default function ContactPage() {
  const { t } = useLanguage();
  useSEO({ title: t.contact.seo.title, description: t.contact.seo.description, path: "/contact" });
  const c = t.contact;

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle | sending | success | error

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = c.form.errors.name;
    if (!EMAIL_RE.test(form.email.trim())) errs.email = c.form.errors.email;
    if (form.subject.trim().length < 2) errs.subject = c.form.errors.subject;
    if (form.message.trim().length < 10) errs.message = c.form.errors.message;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setState("sending");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organisation: form.organisation.trim() || null,
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setState("success");
    } catch {
      setState("error");
    }
  };

  const inputCls =
    "w-full rounded-md border border-input bg-card px-4 py-2.5 text-sm transition-colors duration-200 focus:border-ochre placeholder:text-muted-foreground/60";
  const labelCls = "block text-sm font-semibold mb-1.5";
  const errCls = "mt-1.5 text-xs text-destructive";

  return (
    <div data-testid="contact-page" className="container-site py-14 sm:py-20 lg:py-28">
      <Reveal>
        <p className="eyebrow">{c.eyebrow}</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{c.title}</h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">{c.intro}</p>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-10">
        <div className="lg:col-span-4 space-y-6">
          <Reveal>
            <figure
              className="overflow-hidden rounded-xl border border-border shadow-sm max-w-md mx-auto lg:mx-0 lg:max-w-none"
              data-testid="contact-portrait-frame"
            >
              <img
                src="/contact-portrait.jpg"
                alt={c.portraitAlt}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
                style={{ objectPosition: "50% 22%" }}
              />
              <figcaption className="border-t border-border bg-card px-5 py-4">
                <p className="font-display font-bold text-sm sm:text-base">{c.portraitName}</p>
                <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-snug">{c.portraitRole}</p>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold">{c.direct.title}</h2>
              <p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">
                {c.direct.emailLabel}
              </p>
              <a
                href={`mailto:${SITE.contactEmail}`}
                data-testid="contact-email-link"
                className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-ochre hover:text-ochre-hover break-all"
              >
                <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                {SITE.contactEmail}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-xl border border-border bg-ink text-sand p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold text-white">{c.identity.title}</h2>
              <p className="mt-3 text-sm text-sand/75 leading-relaxed">{c.identity.line1}</p>
              <p className="mt-2 text-sm text-sand/75 leading-relaxed">{c.identity.line2}</p>
              <p className="mt-4 flex items-center gap-2 text-xs font-mono text-sand/50">
                <Globe className="h-3.5 w-3.5" aria-hidden="true" />
                {c.identity.siteLabel} — www.congobourse.org
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-10">
              {state === "success" ? (
                <div className="text-center py-10" data-testid="contact-success-message" role="status">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden="true" />
                  <h2 className="mt-4 font-display text-2xl font-bold">{c.form.successTitle}</h2>
                  <p className="mt-3 max-w-md mx-auto text-muted-foreground">{c.form.success}</p>
                  <button
                    type="button"
                    data-testid="contact-send-again-button"
                    onClick={() => {
                      setForm(initial);
                      setState("idle");
                    }}
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-2.5 text-sm font-semibold text-ink transition-all duration-200 hover:border-ochre hover:text-ochre"
                  >
                    {c.form.again}
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} noValidate data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className={labelCls}>
                        {c.form.name} *
                      </label>
                      <input
                        id="contact-name"
                        data-testid="contact-form-name-input"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        placeholder={c.form.namePlaceholder}
                        className={inputCls}
                        autoComplete="name"
                        required
                        maxLength={100}
                      />
                      {errors.name && <p className={errCls} data-testid="contact-error-name">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-email" className={labelCls}>
                        {c.form.email} *
                      </label>
                      <input
                        id="contact-email"
                        data-testid="contact-form-email-input"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        placeholder={c.form.emailPlaceholder}
                        className={inputCls}
                        autoComplete="email"
                        required
                        maxLength={150}
                      />
                      {errors.email && <p className={errCls} data-testid="contact-error-email">{errors.email}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-organisation" className={labelCls}>
                        {c.form.organisation}
                      </label>
                      <input
                        id="contact-organisation"
                        data-testid="contact-form-organisation-input"
                        type="text"
                        value={form.organisation}
                        onChange={set("organisation")}
                        placeholder={c.form.organisationPlaceholder}
                        className={inputCls}
                        autoComplete="organization"
                        maxLength={150}
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className={labelCls}>
                        {c.form.subject} *
                      </label>
                      <input
                        id="contact-subject"
                        data-testid="contact-form-subject-input"
                        type="text"
                        value={form.subject}
                        onChange={set("subject")}
                        placeholder={c.form.subjectPlaceholder}
                        className={inputCls}
                        required
                        maxLength={150}
                      />
                      {errors.subject && <p className={errCls} data-testid="contact-error-subject">{errors.subject}</p>}
                    </div>
                  </div>
                  <div className="mt-5">
                    <label htmlFor="contact-message" className={labelCls}>
                      {c.form.message} *
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-form-message-input"
                      value={form.message}
                      onChange={set("message")}
                      placeholder={c.form.messagePlaceholder}
                      className={`${inputCls} min-h-36 resize-y`}
                      required
                      maxLength={3000}
                    />
                    {errors.message && <p className={errCls} data-testid="contact-error-message">{errors.message}</p>}
                  </div>

                  {/* Honeypot — invisible to humans */}
                  <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden">
                    <label htmlFor="contact-website">Website</label>
                    <input
                      id="contact-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={set("website")}
                    />
                  </div>

                  {state === "error" && (
                    <div
                      className="mt-5 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
                      role="alert"
                      data-testid="contact-error-message-banner"
                    >
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                      <p>
                        <strong>{c.form.errorTitle}.</strong> {c.form.error}{" "}
                        <a href={`mailto:${SITE.contactEmail}`} className="underline font-semibold">
                          {SITE.contactEmail}
                        </a>
                        .
                      </p>
                    </div>
                  )}

                  <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                    <button
                      type="submit"
                      data-testid="contact-form-submit-button"
                      disabled={state === "sending"}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-7 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-ochre hover:shadow-md active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <Send className="h-4 w-4" aria-hidden="true" />
                      {state === "sending" ? c.form.sending : c.form.submit}
                    </button>
                    <p className="text-xs text-muted-foreground">{c.privacy}</p>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
